// Centralised client-side validators.
//
// Backend remains the source of truth — these helpers exist for fast
// inline UX feedback and as a "sanitize -> validate -> submit"
// payload-builder gate. They are deliberately stricter than the
// individual regexes scattered across the codebase, but they MUST
// stay aligned with the backend's Joi schemas so a frontend "valid"
// never disagrees with the backend (causing a confusing 400 the user
// can't recover from).
//
// Re-exports the legacy helpers from utils/validators.js so existing
// imports keep working while the rest of the app migrates here.

export {
  isGstin,
  isMasterUsername,
  isSubUsername,
  isMobileIN,
  isEmail,
  passwordStrength
} from './validators';

// ---- text / required ----

export function isNonEmptyString(value, { min = 1, max = 255 } = {}) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return v.length >= min && v.length <= max;
}

// ---- login email (only @master.glass-i / @user.glass-i are valid) ----

const LOGIN_EMAIL_RE = /^[a-z0-9][a-z0-9._-]*@(master|user)\.glass-i$/;

export function isLoginEmail(value) {
  return typeof value === 'string' && LOGIN_EMAIL_RE.test(value.toLowerCase());
}

// ---- OTP ----

const OTP_RE = /^[0-9]{4,8}$/;

export function isOtpDigits(value) {
  return typeof value === 'string' && OTP_RE.test(value);
}

// ---- amount in paise ----

export function isAmountPaise(value) {
  return Number.isInteger(value) && value > 0 && value <= 9_999_999_99;
}

// ---- billing cycle / role / enum helpers ----

export function isBillingCycle(value) {
  return value === 'monthly' || value === 'annual';
}

const ROLE_VALUES = new Set(['admin', 'manager', 'staff', 'viewer']);

export function isSubUserRole(value) {
  return ROLE_VALUES.has(value);
}

// ---- positive integer / range ----

export function isPositiveInteger(value, { min = 1, max = 1000 } = {}) {
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
}

export function isUserCount(value) {
  return isPositiveInteger(value, { min: 1, max: 1000 });
}

// ---- Razorpay identifiers ----

const RZP_ORDER_RE = /^order_[A-Za-z0-9_]+$/;
const RZP_PAYMENT_RE = /^pay_[A-Za-z0-9_]+$/;

export function isRazorpayOrderId(value) {
  return typeof value === 'string' && RZP_ORDER_RE.test(value);
}

export function isRazorpayPaymentId(value) {
  return typeof value === 'string' && RZP_PAYMENT_RE.test(value);
}

export function isInrCurrency(value) {
  return value === 'INR';
}

// ---- UUID (v1..v5 — accept any well-formed UUID string) ----

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}
